import * as React from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { setCaretPosition, getInputSelection } from "./util/caret-util";
import { isTouchEnabled } from "./util/touch-util";
import getCaretCoordinates from "textarea-caret";
import { TriggerKeys } from "./constants/TriggerKeys";
import { getTransliterateSuggestions } from "./util/suggestions-util";
import TextareaAutosize from "react-textarea-autosize";
import { useLogger } from "../../../utils/UnifiedLogger";
import PropTypes from "prop-types";






const KEY_UP = "ArrowUp";
const KEY_DOWN = "ArrowDown";
const KEY_LEFT = "ArrowLeft";
const KEY_RIGHT = "ArrowRight";
const KEY_ESCAPE = "Escape";

const OPTION_LIST_Y_OFFSET = 10;
const OPTION_LIST_MIN_WIDTH = 100;

export const KpmgTransliterate = (props) => {
    const { logInfo, logError } = useLogger("KpmgTransliterate", false);
    const {
        renderComponent = (props) => (
            <TextareaAutosize
                {...props}
                minRows={1}
                maxRows={2}
            />
        ),
        lang = "hi",
        offsetX = 0,
        offsetY = 10,
        onChange,
        onChangeText,
        onBlur,
        value,
        onKeyDown,
        containerClassName = "",
        containerStyles = {},
        activeItemStyles = {},
        maxOptions = 5,
        suggestionPosition = "top",
        hideSuggestionBoxOnMobileDevices = false,
        hideSuggestionBoxBreakpoint = 450,
        triggerKeys = [
            TriggerKeys.KEY_SPACE,
            TriggerKeys.KEY_ENTER,
            TriggerKeys.KEY_RETURN,
            TriggerKeys.KEY_TAB,
        ],
        insertCurrentSelectionOnBlur = true,
        showCurrentWordAsLastSuggestion = true,
        enabled = true,
        horizontalView = false,
        apiKey = "",
        ...rest
    } = props;


    const [left, setLeft] = useState(0);
    const [top, setTop] = useState(0);
    const [selection, setSelection] = useState(0);
    const [matchStart, setMatchStart] = useState(-1);
    const [matchEnd, setMatchEnd] = useState(-1);
    const inputRef = useRef(null);
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
    const [direction, setDirection] = useState("ltr");
    const [googleFont, setGoogleFont] = useState(null);
    const [options, setOptions] = useState([]);
    const [logJsonArray, setLogJsonArray] = useState([]);
    const [numSpaces, setNumSpaces] = useState(0);
    const [parentUuid, setParentUuid] = useState("0");
    const [uuid, setUuid] = useState(
        Math.random().toString(36).slice(2, 11)
    );

    const [subStrLength, setSubStrLength] = useState(0);
    const [restart, setRestart] = useState(true);

    const shouldRenderSuggestions = useMemo(
        () =>
            hideSuggestionBoxOnMobileDevices
                ? windowSize.width > hideSuggestionBoxBreakpoint
                : true,
        [windowSize, hideSuggestionBoxBreakpoint, hideSuggestionBoxOnMobileDevices],
    );

    const reset = () => {
        setSelection(0);
        setOptions([]);
    }

    const handleSelection = (index) => {
        const currentString = value;
        const newValue =
            currentString.substring(0, matchStart) +
            options[index] +
            " " +
            currentString.substring(matchEnd + 1, currentString.length);

        if (logJsonArray.length) {
            let lastLogJson = logJsonArray[logJsonArray.length - 1];
            let logJson = {
                keystrokes: lastLogJson.keystrokes,
                results: lastLogJson.results,
                opted: options[index],
                created_at: new Date().toISOString(),
                language: lang
            };
            setLogJsonArray([...logJsonArray, logJson]);
            setNumSpaces(numSpaces + 1);
        }

        setTimeout(() => {
            setCaretPosition(inputRef.current, matchStart + options[index].length + 1);
        }, 1);

        const e = {
            target: { value: newValue },
        };
        onChangeText(newValue);
        onChange && onChange(e);
        reset();
        return inputRef.current?.focus();
    };

    const renderSuggestions = async (lastWord, wholeText) => {
        if (!shouldRenderSuggestions) {
            return;
        }

        if (lang !== 'en') {
            const data = await getTransliterateSuggestions(lastWord, {
                showCurrentWordAsLastSuggestion,
                lang,
            });
            setOptions(data ?? []);

            let logJson = {
                keystrokes: wholeText,
                results: data,
                opted: "",
                created_at: new Date().toISOString(),
                language: lang
            };

            if (restart) {
                setRestart(false);
                setLogJsonArray([logJson]);
            } else {
                setLogJsonArray([...logJsonArray, logJson]);
            }
        } else {
            setOptions([lastWord]);
        }

    };

    const getDirectionAndFont = async (lang) => {
        const langList = ['en', 'hi'];
        const langObj = langList?.find((l) => l.LangCode === lang);
        return [
            langObj?.Direction ?? "ltr",
            langObj?.GoogleFont,
            langObj?.FallbackFont,
        ];
    };

    const handleChange = (e) => {
        const value = e.currentTarget.value;
        logInfo("INPUT_CHANGED", "Input changed", { value });


        if (numSpaces === 0 || restart) {
            if (value.length >= 4) {
                setSubStrLength(value.length - 4);
            } else {
                setSubStrLength(0);
            }
        }

        onChange && onChange(e);
        onChangeText(value);

        if (!shouldRenderSuggestions) {
            return;
        }

        const caret = getInputSelection(e.target).end;
        const input = inputRef.current;

        if (!input) return;

        const caretPos = getCaretCoordinates(input, caret);

        const indexOfLastSpace =
            value.lastIndexOf(" ", caret - 1) < value.lastIndexOf("\n", caret - 1)
                ? value.lastIndexOf("\n", caret - 1)
                : value.lastIndexOf(" ", caret - 1);

        setMatchStart(indexOfLastSpace + 1);
        setMatchEnd(caret - 1);

        const currentWord = value.slice(indexOfLastSpace + 1, caret);
        if (currentWord && enabled) {
            if (numSpaces === 0 || restart) {
                if (value.length >= 4) {
                    renderSuggestions(currentWord, value.substr(value.length - 4, value.length));
                } else {
                    renderSuggestions(currentWord, value.substr(0, value.length));
                }
            } else {
                renderSuggestions(currentWord, value.substr(subStrLength, value.length));
            }

            const rect = input.getBoundingClientRect();

            const left = Math.min(
                caretPos.left,
                rect.width - OPTION_LIST_MIN_WIDTH / 2,
            );
            const top = suggestionPosition === "top"
                ? Math.max(caretPos.top - OPTION_LIST_Y_OFFSET - 30, 0)
                : Math.min(caretPos.top + OPTION_LIST_Y_OFFSET, rect.height);

            setTop(top);
            setLeft(left);
        } else {
            reset();
        }
    };

    const handleKeyDown = (event) => {
        const helperVisible = options.length > 0;

        if (helperVisible) {
            if (triggerKeys.includes(event.key)) {
                event.preventDefault();
                handleSelection(selection);
            } else {
                switch (event.key) {
                    case KEY_ESCAPE:
                        event.preventDefault();
                        reset();
                        break;
                    case KEY_UP:
                        event.preventDefault();
                        setSelection((options.length + selection - 1) % options.length);
                        break;
                    case KEY_DOWN:
                        event.preventDefault();
                        setSelection((selection + 1) % options.length);
                        break;
                    case KEY_LEFT:
                        event.preventDefault();
                        setSelection((options.length + selection - 1) % options.length);
                        break;
                    case KEY_RIGHT:
                        event.preventDefault();
                        setSelection((selection + 1) % options.length);
                        break;
                    default:
                        onKeyDown && onKeyDown(event);
                        break;
                }
            }
        } else {
            onKeyDown && onKeyDown(event);
        }
    };

    const handleBlur = (event) => {
        if (!isTouchEnabled()) {
            if (insertCurrentSelectionOnBlur && options[selection]) {
                handleSelection(selection);
            } else {
                reset();
            }
        }
        onBlur && onBlur(event);
    };

    const handleResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWindowSize({ width, height });
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWindowSize({ width, height });

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        getDirectionAndFont(lang).then(([direction, googleFont, fallbackFont]) => {
            setDirection(direction);
            if (googleFont) {
                if (!document.getElementById(`font-${googleFont}`)) {
                    const link = document.createElement("link");
                    link.id = `font-${googleFont}`;
                    link.href = `https://fonts.googleapis.com/css?family=${googleFont}`;
                    link.rel = "stylesheet";
                    document.head.appendChild(link);
                }
                setGoogleFont(`${googleFont}, ${fallbackFont ?? "sans-serif"}`);
            } else {
                setGoogleFont(null);
            }
        });
    }, [lang]);

    return (
        <div
            style={{
                ...containerStyles,
                position: "relative",
            }}
            className={containerClassName}
        >
            {renderComponent({
                onChange: handleChange,
                onKeyDown: handleKeyDown,
                onBlur: handleBlur,
                ref: inputRef,
                value: value,
                "data-testid": "rt-input-component",
                lang: lang,
                style: {
                    direction: direction,
                    ...(googleFont && { fontFamily: googleFont }),
                },
                ...rest,
            })}
            {shouldRenderSuggestions && options.length > 0 && (
                <ul
                    className={`
    absolute z-[20000] text-sm 
    ${horizontalView ? "flex flex-row" : "flex flex-col"}
    bg-white dark:bg-gray-800 
    border border-gray-300 dark:border-gray-700 
    shadow-lg 
    rounded-md 
    overflow-hidden 
    text-center
    p-1
  `}
                    style={{
                        left: `${left + offsetX}px`,
                        top: `${top + offsetY}px`,
                        fontFamily: googleFont || undefined,
                        transform: "translateY(-100%)",
                    }}
                    data-testid="rt-suggestions-list"
                    lang={lang}
                >
                    {Array.from(new Set(options)).map((item, index) => (
                        <li
                            key={item}
                            onMouseEnter={() => setSelection(index)}
                            onClick={() => handleSelection(index)}
                            className={`
        cursor-pointer px-4 py-2 min-w-[100px] 
        transition-colors duration-200 
        ${index === selection
                                    ? 'bg-[#F95E00] text-white dark:bg-[#9f4b17]'
                                    : 'bg-white text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'}
      `}
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


KpmgTransliterate.propTypes = {
    renderComponent: PropTypes.func,
    lang: PropTypes.string,
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
    onChange: PropTypes.func,
    onChangeText: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.string.isRequired,
    onKeyDown: PropTypes.func,
    containerClassName: PropTypes.string,
    containerStyles: PropTypes.object,
    activeItemStyles: PropTypes.object,
    maxOptions: PropTypes.number,
    suggestionPosition: PropTypes.string,
    hideSuggestionBoxOnMobileDevices: PropTypes.bool,
    hideSuggestionBoxBreakpoint: PropTypes.number,
    triggerKeys: PropTypes.arrayOf(PropTypes.string),
    insertCurrentSelectionOnBlur: PropTypes.bool,
    showCurrentWordAsLastSuggestion: PropTypes.bool,
    enabled: PropTypes.bool,
    horizontalView: PropTypes.bool,
    apiKey: PropTypes.string,
};